<template>
    <div class="ppv2-p-6">
        <Header :title="$t('configuration.integration.title')" />

        <div v-if="loading" class="d-flex justify-content-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
            </div>
        </div>

        <div v-else class="row">
            <div v-for="integration in integrations" :key="integration.id" class="col-12 col-md-6 col-lg-4 ppv2-mb-4">
                <IntegrationCard
                    :integration="integration"
                    :total-outils="totalOutils"
                    @toggle="handleToggle"
                    @settings="openSettings"
                    @update-position="handleUpdatePosition"
                />
            </div>
        </div>

        <!-- Settings Modal -->
        <IntegrationSettingsModal v-model="showSettingsModal" :integration="selectedIntegration" @success="fetchIntegrations" />
    </div>
</template>

<script>
import axios from 'axios'
import IntegrationCard from './components/IntegrationCard.vue'
import IntegrationSettingsModal from './components/IntegrationSettingsModal.vue'
import normalizeSize from '../../../utils/normalizeSize'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import Header from '../../Shared/Header.vue'

export default {
    name: 'IntegrationIndex',
    components: {
        IntegrationCard,
        IntegrationSettingsModal,
        Header,
    },
    props: {
        status: {
            type: String,
            default: '',
        },
        error: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            integrations: [],
            loading: true,
            showSettingsModal: false,
            selectedIntegration: null,
        }
    },
    computed: {
        totalOutils() {
            return this.integrations.filter((i) => i.type === 'outil').length
        },
    },
    mounted() {
        this.fetchIntegrations()
        if (this.status) {
            this.$toast.success(this.status)
        }
        if (this.error) {
            this.$toast.error(this.error)
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchIntegrations() {
            try {
                const response = await axios.get('/back-office/v3/integration/fetch')
                this.integrations = response.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },
        async handleToggle(integration) {
            const newStatus = !integration.active
            const previousStatus = integration.active

            // Validation: if activating, check if at least one setting is filled
            if (newStatus && integration.settings) {
                const hasFilledSetting = integration.settings.some((s) => s.value && s.value.trim() !== '')
                if (!hasFilledSetting) {
                    this.$toast.info(this.$t('configuration.integration.activationError'))
                    return
                }
            }

            // Mise à jour optimiste : le switch bascule immédiatement. Si le
            // serveur refuse (ex. clés Mailjet invalides), on ré-assigne
            // integration.active à sa valeur précédente : ce changement de
            // valeur force Vue à ré-appliquer `:checked` et le switch se
            // ré-affiche à l'état réel (off).
            integration.active = newStatus

            try {
                const response = await axios.post('/back-office/v3/integration/toggle-status', {
                    id: integration.id,
                    active: newStatus,
                })

                if (response.data.success) {
                    this.$toast.success(response.data.message)
                    // Update local state
                    integration.active = newStatus
                } else {
                    // Activation refusée côté serveur (ex. clés Mailjet invalides) :
                    // rendre l'état réel, le switch revient en position off.
                    this.$toast.error(response.data.message)
                    integration.active = previousStatus
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error toggling integration status:', error)
                this.$toast.error(this.$t('configuration.integration.error'))
                // Revert switch state in UI if needed (though fetchIntegrations would also fix it)
                integration.active = previousStatus
                this.fetchIntegrations()
            }
        },
        openSettings(integration) {
            // Alyas / Callbot: the gear is NOT a settings modal — it signs the
            // single-use handshake and opens callbot's activation in a NEW WINDOW
            // (same "gear = action" pattern as Google Calendar).
            if (integration.action === 'activate_callbot') {
                return this.activateCallbot()
            }
            this.selectedIntegration = integration
            this.showSettingsModal = true
        },
        handleSettings(integration) {
            // This is now handled by openSettings for modal-based integrations
            // But we can keep it for integrations that need a direct redirect
            if (integration.settings_url && integration.settings_url !== '#') {
                window.location.href = integration.settings_url
            } else {
                this.openSettings(integration)
            }
        },
        async activateCallbot() {
            try {
                const { data } = await axios.post('/back-office/v3/integration/activate-callbot')
                if (data && data.url) {
                    window.open(data.url, '_blank', 'noopener')
                } else {
                    this.$toast.error(data?.error || this.$t('configuration.integration.error'))
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.error || this.$t('configuration.integration.error'))
                console.error('Error activating Alyas/callbot:', error)
            }
        },
        async handleUpdatePosition(integration, newPosition) {
            try {
                const response = await axios.post('/back-office/v3/integration/update-outil-position', {
                    id: integration.id,
                    position: parseInt(newPosition, 10),
                })

                if (response.data.success) {
                    this.$toast.success(response.data.message)
                    // Re-fetch to get the updated list
                    this.fetchIntegrations()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error updating integration position:', error)
                this.$toast.error(this.$t('messages.error_occurred') || 'Une erreur est survenue')
            }
        },
    },
}
</script>

<style scoped>
.title-header {
    letter-spacing: -0.02em;
}
</style>
